import { useEffect, useState, useSyncExternalStore } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { DEFAULT_POLICY, POLICY_FIELD_NAMES, POLICY_FIELDS, PolicyFormValues, policySchema } from './schema'

import styles from './PublicApiAccess.module.scss'

import { isApiError } from '@/api'
import { PublicAPIPolicy } from '@/api/generated'
import { getPublicApiAccess, updatePublicApiAccess } from '@/api/internal'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import Input from '@/components/Input'
import { Switch } from '@/components/Switch'
import { useToast } from '@/components/Toast/ToastContext'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectIsInternalSuperAdmin } from '@/store/features/user/userSlice'

const toFormValues = (policy: PublicAPIPolicy | null | undefined): PolicyFormValues =>
  policy
    ? {
        enabled: policy.enabled,
        bucket_capacity: policy.bucket_capacity,
        refill_tokens: policy.refill_tokens,
        refill_period_seconds: policy.refill_period_seconds,
      }
    : DEFAULT_POLICY

interface PublicApiAccessProps {
  enterpriseId: string
  enterpriseName: string
}

const PublicApiAccess = ({ enterpriseId, enterpriseName }: PublicApiAccessProps) => {
  const isInternalSuperAdmin = useSelector(selectIsInternalSuperAdmin)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const canManage = isInternalSuperAdmin && !impersonating
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [error, setError] = useState<string | null>(null)
  const [confirmEnable, setConfirmEnable] = useState<PolicyFormValues | null>(null)
  const queryKey = ['public-api-access', enterpriseId]

  const access = useQuery({
    queryKey,
    queryFn: async () => (await getPublicApiAccess(enterpriseId)).data,
    enabled: canManage,
  })
  const policy = access.data

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<PolicyFormValues>({
    resolver: zodResolver(policySchema),
    defaultValues: DEFAULT_POLICY,
    mode: 'onChange',
  })

  useEffect(() => {
    if (access.isSuccess) reset(toFormValues(policy))
  }, [access.isSuccess, policy, reset])

  const save = useMutation({
    mutationFn: (values: PolicyFormValues) =>
      updatePublicApiAccess(enterpriseId, { ...values, expected_policy_version: policy?.policy_version ?? null }),
    onSuccess: ({ data }) => {
      setError(null)
      queryClient.setQueryData(queryKey, data)
      updateToast(`Saved Public API access (version ${data.policy_version})`, 'success')
    },
    onError: (err) => {
      if (isApiError(err) && err.status === 409 && err.detail?.startsWith('Policy changed')) {
        setError('Someone else changed this policy, so it has been reloaded. Review it and save again.')
        queryClient.invalidateQueries({ queryKey })
        return
      }
      setError((isApiError(err) && err.detail) || 'Failed to save Public API access')
    },
  })

  const submit = (values: PolicyFormValues) => {
    setError(null)
    if (values.enabled && !policy?.enabled) setConfirmEnable(values)
    else save.mutate(values)
  }

  if (!canManage) return null

  return (
    <section className={styles.section} aria-label="Public API access">
      <h4 className={styles.title}>Public API access</h4>
      <p className={styles.hint}>
        Each request uses one token. An organization holds up to the burst size and gets the refill amount back every
        interval; when it runs out, requests get a 429.
      </p>

      {access.isLoading && <p className={styles.hint}>Loading Public API access...</p>}
      {access.isError && <p className={styles.error}>Failed to load Public API access. Please try again.</p>}
      {access.isSuccess && (
        <form className={styles.form} onSubmit={handleSubmit(submit)} noValidate>
          <p className={styles.hint}>
            {policy ? `${policy.enabled ? 'Enabled' : 'Disabled'}, version ${policy.policy_version}` : 'Not configured'}
          </p>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}

          <div className={styles.toggle}>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onInputChange={field.onChange}
                  disabled={save.isPending}
                  aria-label={`Enable the Public API for ${enterpriseName}`}
                />
              )}
            />
            <span>Public API enabled</span>
          </div>

          {POLICY_FIELD_NAMES.map((name) => {
            const { label, hint, min, max } = POLICY_FIELDS[name]
            return (
              <div key={name} className={styles.field}>
                <label className={styles.label} htmlFor={`public-api-${name}`}>
                  {label}
                </label>
                <span className={styles.hint}>
                  {hint} {min}–{max}.
                </span>
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <Input
                      id={`public-api-${name}`}
                      type="number"
                      inputMode="numeric"
                      min={String(min)}
                      max={String(max)}
                      value={String(field.value ?? '')}
                      onInput={field.onChange}
                      isDisabled={save.isPending}
                    />
                  )}
                />
                {errors[name] && <p className={styles.error}>{errors[name]?.message}</p>}
              </div>
            )
          })}

          <div className={styles.actions}>
            <Button
              title={policy ? 'Save changes' : 'Create policy'}
              type="dark"
              htmlType="submit"
              onClick={() => {}}
              isLoading={save.isPending}
              isDisabled={save.isPending || !isValid || (policy != null && !isDirty)}
            />
          </div>
        </form>
      )}

      <ConfirmDialog
        isOpen={confirmEnable !== null}
        title="Enable the Public API?"
        message={`${enterpriseName}'s API keys will start working as soon as you save.`}
        confirmLabel="Enable and save"
        confirmType="dark"
        isConfirming={save.isPending}
        onConfirm={() => {
          if (confirmEnable) save.mutate(confirmEnable, { onSettled: () => setConfirmEnable(null) })
        }}
        onCancel={() => setConfirmEnable(null)}
      />
    </section>
  )
}

export default PublicApiAccess
